import { useState, useMemo } from 'react'
import { Collapse, TableBody, TableHead, useMediaQuery } from '@mui/material'

import { Button } from '../Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { KeyboardArrowDown } from '@assets/svgs/KeyboardArrowDown'
import { KeyboardArrowUp } from '@assets/svgs/KeyboardArrowUp'
import { Pagination } from './components/Pagination'
import { SortableHeader } from './components/SortableHeader'
import { calculateStickyOffsets } from './helpers/calculateStickyOffsets'
import { extractNumericValue } from './helpers/extractNumericValue'
import { LoadingGrid } from '../LoadingGrid'
import { EmptyIcon } from '@assets/svgs/EmptyIcon'
import { Tooltip } from '../Tooltip'

import type { TableProps, SortDirection, GroupedRowData, ColumnDefProps } from './types'

import * as S from './styles'

export const TableGrouped = ({
  data,
  totalData,
  headerTitle,
  headerPeriod,
  footer,
  fixedRow,
  isLoading,
  onSort
}: TableProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const [isCloseTable, setIsCloseTable] = useState(true)
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDirection } | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set())
  const fixedRowElements = Array.isArray(fixedRow?.element) ? fixedRow.element : fixedRow ? [fixedRow.element] : []

  // Verifica se os dados são agrupados
  const isGroupedData = data.length > 0 && 'isParent' in data[0]

  // Obtém as colunas baseado no tipo de dados
  const columns = useMemo(() => {
    if (isGroupedData) {
      const groupedData = data as GroupedRowData[]
      return groupedData[0]?.row || []
    }
    const flatData = data as ColumnDefProps[][]
    return flatData[0] || []
  }, [data, isGroupedData])

  const columnsWithOffsets = calculateStickyOffsets(columns)

  // Toggle de expansão de linha
  const toggleRowExpansion = (rowId: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(rowId)) {
        newSet.delete(rowId)
      } else {
        newSet.add(rowId)
      }
      return newSet
    })
  }

  const handleSort = (columnId: string, direction: SortDirection) => {
    const column = columns.find((col) => col.id === columnId)
    const sortKey = column?.sortKey || columnId

    setSortConfig({ key: sortKey, direction })
    !!onSort &&
      onSort({ sortBy: sortKey, sortType: direction === 'asc' ? 'ASC' : direction === 'desc' ? 'DESC' : null })
  }

  const sortedData = useMemo(() => {
    if (onSort) return data

    if (!sortConfig || sortConfig.direction === 'normal') return data

    if (isGroupedData) {
      const groupedData = data as GroupedRowData[]
      return [...groupedData].sort((a, b) => {
        const aValue = a.row.find((col) => col.id === sortConfig.key)?.value
        const bValue = b.row.find((col) => col.id === sortConfig.key)?.value

        const aNumeric = extractNumericValue(aValue)
        const bNumeric = extractNumericValue(bValue)

        if (aNumeric !== null && bNumeric !== null) {
          return sortConfig.direction === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
        }

        const aStr = String(aValue || '')
        const bStr = String(bValue || '')

        return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
      })
    }

    const flatData = data as ColumnDefProps[][]
    return [...flatData].sort((a, b) => {
      const aValue = a.find((col) => col.id === sortConfig.key)?.value
      const bValue = b.find((col) => col.id === sortConfig.key)?.value

      const aNumeric = extractNumericValue(aValue)
      const bNumeric = extractNumericValue(bValue)

      if (aNumeric !== null && bNumeric !== null) {
        return sortConfig.direction === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      const aStr = String(aValue || '')
      const bStr = String(bValue || '')

      return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
  }, [data, sortConfig, onSort, isGroupedData])

  return (
    <>
      {!!headerTitle && (
        <S.Header>
          <S.HeaderTitle onClick={() => setIsCloseTable(!isCloseTable)}>
            {headerTitle}
            {isCloseTable ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </S.HeaderTitle>

          {!!headerPeriod && <S.HeaderPeriod>{headerPeriod}</S.HeaderPeriod>}
        </S.Header>
      )}

      <S.Container
        style={{
          borderRadius: !!headerTitle ? '0 0 10px 10px' : '10px',
          borderTop: !!headerTitle ? 'none' : `1px solid ${emotionTheme.colors.border.light}`
        }}
      >
        {!!isLoading && !data.length ? (
          <S.GridMessage>
            <LoadingGrid />
          </S.GridMessage>
        ) : !data.length && !isLoading ? (
          <S.GridMessage>
            <EmptyIcon />
            <S.EmptyMessage>Nenhum dado encontrado!</S.EmptyMessage>
          </S.GridMessage>
        ) : (
          <>
            <Collapse in={isCloseTable} unmountOnExit>
              <S.TableWrapper>
                {!!isLoading && !!data.length && (
                  <S.LoaderBox>
                    <LoadingGrid />
                  </S.LoaderBox>
                )}
                <S.TableContent>
                  <S.Table style={{ tableLayout: 'auto', minWidth: '100%' }}>
                    <TableHead>
                      <S.TableRow>
                        {columnsWithOffsets.map((column) => {
                          const headerAlignment = column.headerAlign || column.align
                          return (
                            <S.HeadCell
                              key={column.id}
                              columnQuantity={columns.length}
                              align={headerAlignment}
                              minWidth={column.minWidth || '10rem'}
                              maxWidth={column.maxWidth || '14.5rem'}
                              sticky={!!column.stickyPosition}
                              stickyPosition={column.stickyPosition}
                              stickyOffset={column.stickyOffset}
                              style={{
                                background: column.columnColor
                                  ? column.columnColor
                                  : !!headerTitle
                                    ? emotionTheme.colors.gray[100]
                                    : emotionTheme.colors.blue.head
                              }}
                            >
                              <SortableHeader
                                sortable={column.sortable}
                                align={headerAlignment}
                                onSort={(direction) => handleSort(column.id, direction)}
                                isActive={sortConfig?.key === (column.sortKey || column.id)}
                                currentSortDirection={
                                  sortConfig?.key === (column.sortKey || column.id) ? sortConfig.direction : 'normal'
                                }
                              >
                                {column.columnName}
                              </SortableHeader>
                            </S.HeadCell>
                          )
                        })}
                      </S.TableRow>
                    </TableHead>

                    <TableBody>
                      {isGroupedData
                        ? (sortedData as GroupedRowData[]).map((groupedRow) => {
                            const rowWithOffsets = calculateStickyOffsets(groupedRow.row)
                            const isExpanded = expandedRows.has(groupedRow.id)
                            const hasChildren = groupedRow.children && groupedRow.children.length > 0

                            return (
                              <>
                                <S.TableRow
                                  key={groupedRow.id}
                                  onClick={(event) => {
                                    const target = event.target as HTMLElement
                                    if (target.closest('input[type="checkbox"]')) return

                                    if (hasChildren) {
                                      toggleRowExpansion(groupedRow.id)
                                    }
                                  }}
                                  style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                                  isParent={groupedRow.isParent}
                                >
                                  {rowWithOffsets.map((col, colIndex) => {
                                    const rawValue = col.value
                                    const numericValue = Number(rawValue)
                                    const isNegative = !isNaN(numericValue) && numericValue < 0
                                    const withoutTooltip = col.withoutTooltip
                                    const bodyAlignment = col.bodyAlign || col.align
                                    const isFirstColumn = colIndex === 0
                                    const isActionColumn = col.id === 'action'

                                    return (
                                      <S.TableCell
                                        key={`${groupedRow.id}-${col.id}`}
                                        columnQuantity={columns.length}
                                        align={bodyAlignment}
                                        isNegative={isNegative}
                                        minWidth={col.minWidth || '10rem'}
                                        maxWidth={col.maxWidth || '14.5rem'}
                                        sticky={!!col.stickyPosition}
                                        stickyPosition={col.stickyPosition}
                                        stickyOffset={col.stickyOffset}
                                        columnColor={col.columnColor}
                                        fontWeight={col.fontWeight}
                                        isParent={groupedRow.isParent}
                                        onClick={(e) => {
                                          if (isActionColumn) {
                                            e.stopPropagation()
                                          }
                                        }}
                                      >
                                        <S.TableCellContent>
                                          {isFirstColumn && hasChildren && (
                                            <S.ExpandIcon>
                                              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </S.ExpandIcon>
                                          )}
                                          <Tooltip
                                            title={
                                              !withoutTooltip
                                                ? col.currency
                                                  ? currencyMask(Number(rawValue).toFixed(2))
                                                  : rawValue
                                                : ''
                                            }
                                            placement="top"
                                            delay={1000}
                                          >
                                            <S.TableCellValue>
                                              {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                            </S.TableCellValue>
                                          </Tooltip>
                                        </S.TableCellContent>
                                      </S.TableCell>
                                    )
                                  })}
                                </S.TableRow>
                                {hasChildren &&
                                  isExpanded &&
                                  groupedRow.children?.map((childRow) => {
                                    const childRowWithOffsets = calculateStickyOffsets(childRow.row)

                                    return (
                                      <S.TableRow key={`${groupedRow.id}-child-${childRow.id}`} isParent={false}>
                                        {childRowWithOffsets.map((col) => {
                                          const rawValue = col.value
                                          const numericValue = Number(rawValue)
                                          const isNegative = !isNaN(numericValue) && numericValue < 0
                                          const withoutTooltip = col.withoutTooltip
                                          const bodyAlignment = col.bodyAlign || col.align

                                          return (
                                            <S.TableCell
                                              key={`${childRow.id}-${col.id}`}
                                              columnQuantity={columns.length}
                                              align={bodyAlignment}
                                              isNegative={isNegative}
                                              minWidth={col.minWidth || '10rem'}
                                              maxWidth={col.maxWidth || '14.5rem'}
                                              sticky={!!col.stickyPosition}
                                              stickyPosition={col.stickyPosition}
                                              stickyOffset={col.stickyOffset}
                                              columnColor={col.columnColor}
                                              fontWeight={col.fontWeight}
                                              isParent={false}
                                            >
                                              <Tooltip
                                                title={
                                                  !withoutTooltip
                                                    ? col.currency
                                                      ? currencyMask(Number(rawValue).toFixed(2))
                                                      : rawValue
                                                    : ''
                                                }
                                                placement="top"
                                                delay={1000}
                                              >
                                                <S.TableCellValue>
                                                  {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                                </S.TableCellValue>
                                              </Tooltip>
                                            </S.TableCell>
                                          )
                                        })}
                                      </S.TableRow>
                                    )
                                  })}
                              </>
                            )
                          })
                        : (sortedData as ColumnDefProps[][]).map((row, rowIndex) => {
                            const rowWithOffsets = calculateStickyOffsets(row)

                            return (
                              <S.TableRow key={rowIndex}>
                                {rowWithOffsets.map((col) => {
                                  const rawValue = col.value
                                  const numericValue = Number(rawValue)
                                  const isNegative = !isNaN(numericValue) && numericValue < 0
                                  const withoutTooltip = col.withoutTooltip
                                  const bodyAlignment = col.bodyAlign || col.align

                                  return (
                                    <S.TableCell
                                      key={`${rowIndex}-${col.id}`}
                                      columnQuantity={columns.length}
                                      align={bodyAlignment}
                                      isNegative={isNegative}
                                      minWidth={col.minWidth || '10rem'}
                                      maxWidth={col.maxWidth || '14.5rem'}
                                      sticky={!!col.stickyPosition}
                                      stickyPosition={col.stickyPosition}
                                      stickyOffset={col.stickyOffset}
                                      columnColor={col.columnColor}
                                    >
                                      <Tooltip
                                        title={
                                          !withoutTooltip
                                            ? col.currency
                                              ? currencyMask(Number(rawValue).toFixed(2))
                                              : rawValue
                                            : ''
                                        }
                                        placement="top"
                                        delay={1000}
                                      >
                                        <S.TableCellValue>
                                          {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                        </S.TableCellValue>
                                      </Tooltip>
                                    </S.TableCell>
                                  )
                                })}
                              </S.TableRow>
                            )
                          })}
                    </TableBody>

                    {!!totalData && (
                      <TableBody>
                        {totalData.map((row, rowIndex) => {
                          const rowWithOffsets = calculateStickyOffsets(row)

                          return (
                            <S.TableRow key={rowIndex}>
                              {rowWithOffsets.map((col) => {
                                const rawValue = col.value
                                const numericValue = Number(rawValue)
                                const isNegative = !isNaN(numericValue) && numericValue < 0
                                const withoutTooltip = col.withoutTooltip
                                const bodyAlignment = col.bodyAlign || col.align

                                return (
                                  <S.TableCell
                                    key={`${rowIndex}-${col.id}`}
                                    columnQuantity={columns.length}
                                    align={bodyAlignment}
                                    isNegative={isNegative}
                                    minWidth={col.minWidth || '10rem'}
                                    maxWidth={col.maxWidth || '14.5rem'}
                                    sticky={!!col.stickyPosition}
                                    stickyPosition={col.stickyPosition}
                                    stickyOffset={col.stickyOffset}
                                    columnColor={col.columnColor}
                                    fontWeight={col.fontWeight}
                                  >
                                    <Tooltip
                                      title={
                                        !withoutTooltip
                                          ? col.currency
                                            ? currencyMask(Number(rawValue).toFixed(2))
                                            : rawValue
                                          : ''
                                      }
                                      placement="top"
                                      delay={1000}
                                    >
                                      <S.TableCellValue>
                                        {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                      </S.TableCellValue>
                                    </Tooltip>
                                  </S.TableCell>
                                )
                              })}
                            </S.TableRow>
                          )
                        })}
                      </TableBody>
                    )}
                  </S.Table>
                </S.TableContent>
              </S.TableWrapper>
            </Collapse>

            {fixedRowElements.map((element, index) => (
              <S.AditionalRow key={index}>{element}</S.AditionalRow>
            ))}
          </>
        )}

        {(!!data.length || !isLoading) &&
          (!!footer?.actionButton?.labelButton ||
            (!!footer?.pagination?.totalItems && footer?.pagination?.totalItems > 5)) && (
            <>
              <S.Footer>
                {footer.actionButton && (
                  <Tooltip
                    width={!isMobile ? 'fit-content' : '100%'}
                    title={footer.actionButton.tooltip}
                    placement="top-start"
                    style={{ width: isMobile ? '100%' : 'fit-content' }}
                  >
                    <Button
                      width={isMobile ? '100%' : 'fit-content'}
                      colorType="outlineBlue"
                      onClick={footer.actionButton.onClick}
                      disabled={isLoading || footer.actionButton.disabled}
                      rightIcon={footer.actionButton.rightIcon}
                    >
                      {footer.actionButton.labelButton}
                    </Button>
                  </Tooltip>
                )}

                {!!footer.pagination?.totalItems && footer.pagination?.totalItems > 5 && (
                  <Pagination
                    currentPage={footer.pagination?.currentPage}
                    itemsPerPage={footer.pagination?.itemsPerPage}
                    totalItems={footer.pagination?.totalItems}
                    totalPages={footer.pagination?.totalPages}
                    onPageChange={footer.pagination?.onPageChange}
                    onItemsPerPageChange={footer.pagination?.onItemsPerPageChange}
                  />
                )}
              </S.Footer>
            </>
          )}
      </S.Container>
    </>
  )
}
